import { RegistrationRequiredContent } from '@common/components/registration-required-overlay/registration-required-content';
import { useLang } from '@common/context/language-provider';
import { useAppRouteHelpers } from '@common/hooks/use-app-route-helpers';
import { useGlobalRegistrationRequiredModalStore } from '@common/stores';
import { Modal } from '@konova/ui/modal';
import { SpaceAdminRoutes } from '@routes/space-admin-routes';
import { SurveyRoutes } from '@routes/survey-routes.ts';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useLocation } from 'react-router';

/**
 * GlobalRegistrationRequiredModal is a functional component that displays a modal requiring user registration
 * when certain conditions are met. It interacts with a global store to manage its open state and the login URL.
 *
 * The component utilizes application routes and language settings to dynamically determine the login URL based on
 * the current route or application parameters. It also includes a child component, `RegistrationRequiredContent`,
 * which handles the content of the modal and provides a login button to redirect users to the appropriate login page.
 *
 * Dependencies:
 * - useAppRouteHelpers: A hook providing route-related helper methods.
 * - useGlobalRegistrationRequiredModalStore: A global store managing modal state and login URL.
 * - useHistory: A hook to access the application's route and navigation history.
 * - useLang: A hook to get the current language setting.
 *
 * Props:
 * - None.
 *
 * Behavior:
 * - The modal opens when the `open` property from the global store is set to `true`.
 * - The modal closes when the user clicks the close button or logs in through the login URL.
 * - Login URL generation is dynamic and based on the loginUrl from the store or a fallback path derived from the current route.
 */
export const GlobalRegistrationRequiredModal: FC = () => {
  const appRouteHelpers = useAppRouteHelpers();
  const { open, loginUrl, set } = useGlobalRegistrationRequiredModalStore(state => state);
  const { language: lang } = useLang();
  const location = useLocation();
  const url = useMemo(() => {
    if (loginUrl) {
      return loginUrl;
    }

    const parts = location.pathname.split('/').filter(Boolean);
    if (parts.length > 2) {
      return appRouteHelpers.generateRouteWithCurrentParams(SurveyRoutes.login, {
        lang: parts[0],
        surveySlug: parts[1]
      });
    }

    return generatePath(SpaceAdminRoutes.login, { lang });
  }, [lang, location.pathname, loginUrl]);
  const [t] = useTranslation();

  return (
    <Modal open={open} onClose={() => set({ open: false })} label={t('pages.login.index.form.overlay-title')}>
      <RegistrationRequiredContent
        onLoginClicked={() => set({ open: false })}
        loginUrl={url}
      />
    </Modal>
  );
};